import React, { useState, useEffect, useCallback } from "react";
import ListView from "../../../../components/common/ListView";
import { IconSearch, IconPlus, IconTrash, IconPencil, IconRefresh, IconX } from "@tabler/icons-react";
import {
  fetchHrAttendanceBreaks,
  createHrAttendanceBreak,
  updateHrAttendanceBreak,
  deleteHrAttendanceBreak,
} from "../../../../apis/hrApi";
import InputField from "../../../../components/common/InputField";
import toast from "react-hot-toast";

export default function AttendanceBreakTab() {
  const [breaksData, setBreaksData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit] = useState(10);

  // Sidebar Panel State
  const [sidebarMode, setSidebarMode] = useState<"ADD" | "EDIT" | "DELETE" | null>(null);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  
  // Form Fields State
  const [title, setTitle] = useState("");
  const [allowedInDays, setAllowedInDays] = useState("");
  const [breakTimeMinutes, setBreakTimeMinutes] = useState("");
  const [status, setStatus] = useState<"ACTIVE" | "INACTIVE">("ACTIVE");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadBreaks = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchHrAttendanceBreaks(currentPage, limit, searchQuery);
      if (res.success) {
        // Handle list structures safely
        let list: any[] = [];
        if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.data && typeof res.data === "object") {
          const possible = res.data.attendanceBreaks || res.data.list || res.data.data || res.data.attendanceBreak;
          if (Array.isArray(possible)) {
            list = possible;
          } else {
            const found = Object.values(res.data).find(Array.isArray);
            if (found) list = found as any[];
          }
        }
        setBreaksData(list);
        const total = res.data?.total || res.data?.totalCount || list.length;
        setTotalRecords(total);
        setTotalPages(res.data?.totalPages || Math.ceil(total / limit) || 1);
      } else {
        setError("Failed to load attendance breaks");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load attendance breaks");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, limit, searchQuery]);

  useEffect(() => {
    loadBreaks();
  }, [loadBreaks]);

  // Handle direct switch toggle in table row
  const handleToggleStatus = async (item: any) => {
    try {
      const newStatus = item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      const payload = {
        title: item.title,
        allowedInDays: item.allowedInDays,
        breakTimeMinutes: item.breakTimeMinutes,
        status: newStatus,
      };
      const res = await updateHrAttendanceBreak(item.id || item._id, payload);
      if (res.success) {
        toast.success(`Status updated to ${newStatus}`);
        loadBreaks();
      } else {
        toast.error(res.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  // Open sidebar forms
  const handleOpenAdd = () => {
    setTitle("");
    setAllowedInDays("");
    setBreakTimeMinutes("");
    setStatus("ACTIVE");
    setSelectedItem(null);
    setSidebarMode("ADD");
  };

  const handleOpenEdit = (item: any) => {
    setTitle(item.title || "");
    setAllowedInDays(item.allowedInDays?.toString() || "");
    setBreakTimeMinutes(item.breakTimeMinutes?.toString() || "");
    setStatus(item.status || "ACTIVE");
    setSelectedItem(item);
    setSidebarMode("EDIT");
  };

  const handleOpenDelete = (item: any) => {
    setSelectedItem(item);
    setSidebarMode("DELETE");
  };

  // Submit operations
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Please enter a Title.");
      return;
    }
    if (!allowedInDays.trim()) {
      toast.error("Please enter Allowed In Days.");
      return;
    }
    if (!breakTimeMinutes.trim()) {
      toast.error("Please enter Break Time.");
      return;
    }

    setIsSubmitting(true);
    const payload: any = {
      title: title.trim(),
      allowedInDays: parseInt(allowedInDays, 10),
      breakTimeMinutes: parseInt(breakTimeMinutes, 10),
    };

    if (sidebarMode === "EDIT") {
      payload.status = status;
    }

    try {
      let res;
      if (sidebarMode === "EDIT" && selectedItem) {
        res = await updateHrAttendanceBreak(selectedItem.id || selectedItem._id, payload);
      } else {
        res = await createHrAttendanceBreak(payload);
      }

      if (res.success) {
        toast.success(
          sidebarMode === "EDIT"
            ? "Attendance break updated successfully"
            : "Attendance break created successfully"
        );
        loadBreaks();
        setSidebarMode(null);
        setSelectedItem(null);
      } else {
        toast.error(res.message || "Failed to save");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while saving");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;
    setIsSubmitting(true);
    try {
      const id = selectedItem.id || selectedItem._id;
      const res = await deleteHrAttendanceBreak(id);
      if (res.success) {
        toast.success("Attendance break deleted successfully");
        loadBreaks();
        setSidebarMode(null);
        setSelectedItem(null);
      } else {
        toast.error(res.message || "Failed to delete");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete attendance break");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Column definitions for ListView
  const columns = [
    {
      label: "BREAK TYPE",
      render: (s: any) => <span className="font-semibold text-gray-800 text-sm">{s.title}</span>,
    },
    {
      label: "ALLOWS IN DAYS",
      render: (s: any) => <span className="font-semibold text-[#212121] text-sm">{s.allowedInDays ?? "-"}</span>,
    },
    {
      label: "STATUS",
      render: (s: any) => (
        <button
          type="button"
          onClick={() => handleToggleStatus(s)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
            s.status === "ACTIVE" ? "bg-black" : "bg-gray-200"
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              s.status === "ACTIVE" ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      ),
    },
    {
      label: "ACTIONS",
      render: (s: any) => (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => handleOpenEdit(s)}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition text-slate-500 cursor-pointer bg-white"
            title="Edit"
          >
            <IconPencil size={15} stroke={1.5} />
          </button>
          <button
            onClick={() => handleOpenDelete(s)}
            className="w-8 h-8 rounded-full border border-red-100 flex items-center justify-center hover:bg-red-50 transition text-red-500 cursor-pointer bg-white"
            title="Delete"
          >
            <IconTrash size={15} stroke={1.5} />
          </button>
        </div>
      ),
    },
  ];

  const isSidebarOpen = sidebarMode !== null;

  // Filter columns when sidebar is open to preserve layout readability
  const activeColumns = isSidebarOpen
    ? columns.filter((_, idx) => [0, 2, 3].includes(idx)) // Show BREAK TYPE, STATUS, ACTIONS
    : columns;

  const sidebarContent = sidebarMode ? (
    <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden animate-in slide-in-from-right duration-200">
      <div className="flex items-center justify-between px-5 py-3.5 shrink-0 border-b border-gray-100 bg-white">
        <h2 className="text-lg font-bold text-[#212121]">
          {sidebarMode === "ADD"
            ? "Add Break Type"
            : sidebarMode === "EDIT"
            ? "Edit Break Type"
            : "Delete Shift"}
        </h2>
        <button
          type="button"
          onClick={() => {
            setSidebarMode(null);
            setSelectedItem(null);
          }}
          className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
          title="Close"
        >
          <IconX size={18} />
        </button>
      </div>

      {sidebarMode === "DELETE" ? (
        <div className="p-6 bg-white space-y-6">
          <p className="text-sm text-gray-600 font-medium">
            Are you sure you want to delete this shift type?
          </p>
          {selectedItem && (
            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">TITLE</span>
              <span className="text-sm font-semibold text-gray-800">{selectedItem.title}</span>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] text-xs font-bold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm hover:shadow-md duration-150 hover:-translate-y-0.5 active:translate-y-0"
            >
              {isSubmitting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white p-6 max-h-[calc(100vh-160px)] overflow-y-auto space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
            <InputField
              label="Title *"
              placeholder="Ex. Lunch Break"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
            <InputField
              label="Allowed In Days *"
              placeholder="1"
              type="number"
              value={allowedInDays}
              onChange={(e) => setAllowedInDays(e.target.value)}
              required
            />
          </div>

          <div className="flex justify-between items-center gap-4">
            <div className="flex-1">
              <InputField
                label="Break Time (minutes) *"
                placeholder="30"
                type="number"
                value={breakTimeMinutes}
                onChange={(e) => setBreakTimeMinutes(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1 flex flex-col items-center">
              <label className="text-[#1E293B] text-xs font-semibold block">Status</label>
              <button
                type="button"
                onClick={() => setStatus(status === "ACTIVE" ? "INACTIVE" : "ACTIVE")}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  status === "ACTIVE" ? "bg-black" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    status === "ACTIVE" ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] text-xs font-bold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm hover:shadow-md duration-150 hover:-translate-y-0.5 active:translate-y-0"
            >
              {isSubmitting ? (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#212121]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : null}
              <span>{sidebarMode === "EDIT" ? "Update Break Type" : "Create Break Type"}</span>
            </button>
          </div>
        </form>
      )}
    </div>
  ) : null;

  return (
    <div className="w-full">
      <div className="grid grid-cols-12 gap-6 p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 rounded-r-3xl rounded-bl-3xl w-full mt-2 animate-in fade-in duration-300 items-start">
        <div className={isSidebarOpen ? "col-span-12 xl:col-span-7 transition-all duration-300" : "col-span-12"}>
          <div className="bg-white rounded-3xl border border-gray-200/50 shadow-sm overflow-hidden flex flex-col justify-start">
            <div className="px-6 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100">
              <h2 className="text-lg font-medium text-[#212121]">Attendance Break</h2>
              <div className="flex items-center flex-wrap gap-2.5 justify-start sm:justify-end">
                {/* Search Sliding Input Box */}
                <div className="relative flex items-center group">
                  <div
                    onClick={() => {
                      if (!showSearch) {
                        setShowSearch(true);
                      }
                    }}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch
                        ? "w-48 px-2 cursor-default"
                        : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                      }}
                      className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                    >
                      <IconSearch size={17} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          placeholder="Search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none animate-in fade-in duration-200"
                        />
                        {searchQuery && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchQuery("");
                            }}
                            className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                          >
                            <IconX size={14} stroke={2.5} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Refresh Button */}
                <button
                  onClick={loadBreaks}
                  className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                  title="Refresh"
                >
                  <IconRefresh size={17} stroke={2.5} />
                </button>

                {/* Add Button */}
                <button
                  onClick={handleOpenAdd}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#e2bd30] border border-[#FBD037] text-[#212121] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer"
                  title="Add Attendance Break"
                >
                  <IconPlus stroke={2.5} size={17} />
                </button>
              </div>
            </div>

            <div className="pb-2">
              {isLoading ? (
                <div className="p-8 flex justify-center items-center">
                  <svg
                    className="animate-spin h-6 w-6 text-[#FBD037]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                </div>
              ) : error ? (
                <div className="p-8 text-center text-red-500 font-semibold">{error}</div>
              ) : breaksData.length === 0 ? (
                <div className="p-8 text-center text-gray-400 font-medium">No Attendance Breaks Found</div>
              ) : (
                <>
                  <ListView
                    mainDivClassName="bg-transparent"
                    tdClassName="rounded-r-none rounded-l-none"
                    trClassName="hover:bg-[#FBD037]/20 transition-colors border-b border-gray-100"
                    mailDivClassName="p-0 bg-transparent"
                    thClassName="border-t"
                    columns={activeColumns}
                    data={breaksData}
                  />
                  <div className="flex justify-end px-6 py-4 text-sm text-gray-500 items-center gap-2">
                    <span>
                      Record {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, totalRecords)} of{" "}
                      {totalRecords}
                    </span>
                    <span className="mx-2">{limit}</span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="opacity-50 disabled:opacity-20 cursor-pointer"
                    >
                      &lt;
                    </button>
                    <span>
                      {currentPage}/{totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="opacity-50 disabled:opacity-20 cursor-pointer"
                    >
                      &gt;
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Sidebar (static column inside grid) */}
        {isSidebarOpen && (
          <div className="hidden xl:block xl:col-span-5 space-y-4 mt-2">
            {sidebarContent}
          </div>
        )}
      </div>

      {/* Mobile/Tablet Sidebar (fixed overlay outside grid) */}
      {isSidebarOpen && (
        <div className="xl:hidden">
          <div
            className="fixed inset-0 bg-black/40 z-30"
            onClick={() => {
              setSidebarMode(null);
              setSelectedItem(null);
            }}
          />
          <div className="fixed inset-y-0 right-0 z-[35] w-full sm:w-[500px] p-4 pt-[76px] sm:pt-[96px] shadow-2xl bg-[#F7F6F2] overflow-y-auto flex flex-col justify-start">
            {sidebarContent}
          </div>
        </div>
      )}
    </div>
  );
}
